import { useMemo } from "react";

import { UNIT_IDS, UNIT_LABELS, UnitSystem } from "types/units";

import { useEvaUnitCore } from "./useEvaUnitCore";
import useUserUnit from "./useUserUnit";

const SQUARE_METERS_PER_SQUARE_KILOMETER = 1e6;
const SQUARE_FEET_PER_SQUARE_MILE = 27878400;

interface LassoAreaFormatterResult {
  formatArea: (areaInSquareMeters: number) => string;
}

export function useLassoAreaFormatter(): LassoAreaFormatterResult {
  const unitType = useUserUnit();
  const { convertUnitToUnit, isReady } = useEvaUnitCore();

  const formatArea = useMemo(
    () =>
      (areaInSquareMeters: number): string => {
        if (!areaInSquareMeters || areaInSquareMeters === 0) {
          return unitType === UnitSystem.Imperial
            ? `0.00 ${UNIT_LABELS.SQUARE_FEET}`
            : `0.00 ${UNIT_LABELS.SQUARE_METERS}`;
        }

        // If unit core isn't ready, fall back to displaying in m²
        if (!isReady) {
          return `${areaInSquareMeters.toFixed(2)} ${UNIT_LABELS.SQUARE_METERS}`;
        }

        if (unitType === UnitSystem.Imperial) {
          const areaInSquareFeet = convertUnitToUnit(
            UNIT_IDS.SQUARE_METERS,
            UNIT_IDS.SQUARE_FEET,
            areaInSquareMeters,
            2
          );

          // Convert to mi² if >= 27,878,400 ft²
          if (areaInSquareFeet >= SQUARE_FEET_PER_SQUARE_MILE) {
            // First convert m² to km², then km² to mi²
            const areaInSquareKilometers = convertUnitToUnit(
              UNIT_IDS.SQUARE_METERS,
              UNIT_IDS.SQUARE_KILOMETERS,
              areaInSquareMeters,
              -1
            );
            const areaInSquareMiles = convertUnitToUnit(
              UNIT_IDS.SQUARE_KILOMETERS,
              UNIT_IDS.SQUARE_MILES,
              areaInSquareKilometers,
              2
            );
            return `${areaInSquareMiles.toFixed(2)} ${UNIT_LABELS.SQUARE_MILES}`;
          }

          return `${areaInSquareFeet.toFixed(2)} ${UNIT_LABELS.SQUARE_FEET}`;
        }

        // Convert to km² if >= 1,000,000 m²
        if (areaInSquareMeters >= SQUARE_METERS_PER_SQUARE_KILOMETER) {
          const areaInSquareKilometers = convertUnitToUnit(
            UNIT_IDS.SQUARE_METERS,
            UNIT_IDS.SQUARE_KILOMETERS,
            areaInSquareMeters,
            2
          );
          return `${areaInSquareKilometers.toFixed(2)} ${UNIT_LABELS.SQUARE_KILOMETERS}`;
        }

        return `${areaInSquareMeters.toFixed(2)} ${UNIT_LABELS.SQUARE_METERS}`;
      },
    [unitType, convertUnitToUnit, isReady]
  );

  return { formatArea };
}

export default useLassoAreaFormatter;
